/**
 * Work & Deliverables — every assignee's work, for a manager.
 *
 * Distinct from My Work: this hits /service/work-items, which accepts an
 * assignee filter and is manager-gated. My Work hits a different endpoint that
 * takes no such parameter.
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, type WorkItem } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';

const STATUSES = [
  '', 'not_started', 'in_progress', 'internal_review', 'client_approval',
  'completed', 'blocked',
];

export function ServiceWorkPage() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState(params.get('status') ?? '');
  const [overdueOnly, setOverdueOnly] = useState(params.get('overdueOnly') === 'true');

  const { data, isLoading } = useQuery({
    queryKey: ['svc', 'work-items', status, overdueOnly],
    queryFn: () => api.getWorkItems({ status: status || undefined, overdueOnly }),
  });
  const rows = (data?.data ?? []) as WorkItem[];

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-serif font-semibold tracking-tight">Work &amp; Deliverables</h1>
        <p className="text-sm text-muted-foreground mt-1">
          All work across clients and assignees.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        {STATUSES.map((s) => (
          <button
            key={s || 'all'}
            onClick={() => setStatus(s)}
            className={`px-2.5 py-1 rounded-md text-xs border capitalize transition-colors ${
              status === s ? 'bg-primary text-primary-foreground border-primary'
                           : 'bg-background hover:bg-muted border-input'
            }`}
          >
            {s ? s.replace('_', ' ') : 'All'}
          </button>
        ))}
        <button
          onClick={() => setOverdueOnly((v) => !v)}
          className={`ml-2 px-2.5 py-1 rounded-md text-xs border transition-colors ${
            overdueOnly ? 'bg-destructive text-destructive-foreground border-destructive'
                        : 'bg-background hover:bg-muted border-input'
          }`}
        >
          Overdue only
        </button>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-6 py-10 text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="px-6 py-10 text-center text-sm text-muted-foreground">
              Nothing matches that filter.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Task</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Cycle</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((w) => (
                  <TableRow key={w.id}>
                    <TableCell className="font-medium max-w-xs truncate">{w.title}</TableCell>
                    <TableCell className="text-sm">{w.account_name}</TableCell>
                    <TableCell className="text-sm">{w.cycle_label}</TableCell>
                    <TableCell className="text-sm">{w.assignee_name ?? '—'}</TableCell>
                    <TableCell className="text-sm">
                      {w.due_date ? formatDate(w.due_date) : '—'}
                      {w.is_overdue && <Badge variant="destructive" className="ml-2">late</Badge>}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-sm">
                      {Number(w.actual_hours).toFixed(2)}
                      {w.estimated_hours != null && (
                        <span className="text-muted-foreground"> / {w.estimated_hours}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {w.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
